import {
  CriteriaQuota,
  FeasibilityQuota,
  FeasibilityResponse,
} from '../../../../shared/interfaces/feasibility.interface';
import {
  Criteria,
  RangeSet,
} from '../../../../shared/interfaces/qualification.interface';
import {
  FeasibilityQuota as FeasQuota,
  FeasibilityQuotas,
} from '../../../shared/interfaces/feasibility.interface';

export class StringFormatter {
  constructor(
    public start: string = '',
    public middle: string = '',
    public end: string = ''
  ) {}
}

export class QualificationFormatter {
  constructor(
    public code: number,
    public name: string,
    public rangeFormatter?: StringFormatter
  ) {}

  formatRange(r: RangeSet): string {
    if (!this.rangeFormatter) {
      return `${r.from} - ${r.to}`;
    }
    return `\
${this.rangeFormatter.start}\
${r.from}\
${this.rangeFormatter.middle}\
${r.to}\
${this.rangeFormatter.end}\
`;
  }

  formatAudience(codes: string[]): string {
    return `${codes.length} ${this.name}`;
  }
}

export class AudienceQualificationFormatter {
  constructor(public rangeFormatter?: StringFormatter) {}

  formatRange(r: RangeSet): string {
    return new QualificationFormatter(0, '', this.rangeFormatter).formatRange(
      r
    );
  }

  formatAudience(codes: string[] | Record<number, string>[]): string {
    if (codes.length === 0) {
      return '';
    }

    if (this.isString(codes)) {
      return codes.join(' ');
    }
    return codes.map((code) => Object.values(code).join(' ')).join(' \u00B7 ');
  }

  private isString(values: unknown[]): values is string[] {
    return typeof values[0] === 'string';
  }
}

export class FeasibilityTotal {
  public response_total: number = 0;
  public quotas: FeasibilityQuota[] = [];

  private _commaSeparator = /,/g;

  constructor(
    public readonly response: FeasibilityResponse,
    public readonly quotaSet: Map<number | string, CriteriaQuota[]>
  ) {
    this.response_total = response.total_feasibility;
    this.quotas = response.quotas;
  }

  /**
   * Total feasility calculated here.
   * Min of max feasility and min of each quota category
   * @returns
   */
  public calculate(): number {
    const feasibilities = Array.from(this.quotaSet.values()).map((criteria) => {
      return criteria.reduce(
        (acc, next) => this.count(next.buyer_quota_id) + acc,
        0
      );
    });
    return Math.min(Math.min(...feasibilities), this.response_total);
  }

  public highest(feas: FeasibilityQuotas[]): number {
    return feas
      .map((feasibility) => this.qualificationFeasibility(feasibility.quotas))
      .reduce((acc, next) => Math.max(acc, next), 0);
  }

  public qualificationFeasibility(quotas: FeasQuota[] = []) {
    return (quotas ?? []).reduce((total: number, quota: FeasQuota) => {
      const value = quota.feasibiltyValue || '0';
      return total + Number(value.replace(this._commaSeparator, ''));
    }, 0);
  }

  public count(quotaId: string): number {
    const found = this.quotas.find((quota) => quota.buyer_quota_id === quotaId);
    return found?.count || 0;
  }

  public totalFeasibility(): number {
    return this.response_total;
  }
}

export class FeasibilityCriteria {
  public quotas: FeasibilityQuota[] = [];
  public unusedQualifications: number[] = [];
  public unusedCriterias: { [x: string]: string[] } = {};

  constructor(
    public readonly response: FeasibilityResponse,
    public readonly hasQuota: boolean
  ) {
    this.quotas = response.quotas || [];
    this.unusedQualifications = response.unused_qualifications || [];
    response.unused_criterias?.forEach(
      (unused_criteria: { [x: string]: string[] }) => {
        Object.entries(unused_criteria).forEach(([key, value]) => {
          if (!this.unusedCriterias[key]) {
            this.unusedCriterias[key] = value;
          }
          this.unusedCriterias[key] = [
            ...new Set([...this.unusedCriterias[key], ...value]),
          ];
        });
      }
    );
  }

  public checkUnusedCriteria(
    qualificationCode: number,
    { condition_codes = [] as string[], range_sets = [] as RangeSet[] }
  ): boolean {
    if (this.unusedCriterias[qualificationCode]) {
      if (condition_codes.length) {
        return condition_codes.some((id) =>
          this.unusedCriterias[qualificationCode].includes(id)
        );
      } else if (range_sets.length) {
        return range_sets.some(({ from = '', to = '' }) =>
          this.unusedCriterias[qualificationCode].includes(`${from}-${to}`)
        );
      }
    }
    return false;
  }

  public checkForUnusedQualificationsWithoutQuota(
    unusedQualificationsList: { name: string; code: number }[],
    qualifications: Criteria[],
    unusedQualificationsNameList: string[]
  ) {
    qualifications.forEach((qualification) => {
      const isUnusedCriteria =
        this.unusedQualifications.includes(qualification.qualification_code) ||
        this.checkUnusedCriteria(
          qualification.qualification_code,
          qualification
        );
      if (isUnusedCriteria) {
        if (
          qualification.qualification_name &&
          !unusedQualificationsNameList.includes(
            qualification.qualification_name.toUpperCase()
          )
        ) {
          unusedQualificationsList.push({
            name: qualification.qualification_name.toUpperCase(),
            code: qualification.qualification_code,
          });
        }
      }
    });
  }
}
